import { useState } from "react";
import { CATEGORIES, STATUSES, fmtMoney } from "@/lib/leadMeta";
import { TypeLegend } from "@/components/TypeLegend";
import { MapPin, Flame, MapPinned } from "lucide-react";

// Continental US bounding box
const BOUNDS = { latMax: 49.5, latMin: 24.5, lngMin: -125, lngMax: -66.5 };

const project = (lat, lng) => {
  const x = ((lng - BOUNDS.lngMin) / (BOUNDS.lngMax - BOUNDS.lngMin)) * 100;
  const y = ((BOUNDS.latMax - lat) / (BOUNDS.latMax - BOUNDS.latMin)) * 100;
  return { x, y };
};

export const AreaMap = ({ leads = [], origin = null, radius = 25, onSelect, height = "520px" }) => {
  const [active, setActive] = useState(null);
  const [legendFilter, setLegendFilter] = useState(null);
  const [mode, setMode] = useState("pins");
  const shown = legendFilter ? leads.filter((l) => (l.primary_type || l.insurance_type) === legendFilter) : leads;

  // radius circle diameter as % of map width (approx: lng span 58.5deg ~ 3100mi)
  const milesToPctW = (radius / 3100) * 2 * 100;

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl border border-border bg-[#0a0e17] grid-bg"
      style={{ height }}
      data-testid="area-map"
    >
      {/* faint US label */}
      <div className="pointer-events-none absolute left-4 top-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
        Continental US · {shown.length} leads
      </div>

      {/* view toggle: pins vs heatmap */}
      <div className="absolute right-4 top-3 z-20 flex overflow-hidden rounded-lg border border-white/10 bg-black/50 backdrop-blur" data-testid="map-view-toggle">
        <button
          data-testid="map-mode-pins"
          onClick={() => setMode("pins")}
          className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium transition-colors ${mode === "pins" ? "bg-teal-400/20 text-teal-300" : "text-slate-400 hover:text-slate-200"}`}
        >
          <MapPinned className="h-3.5 w-3.5" /> Pins
        </button>
        <button
          data-testid="map-mode-heat"
          onClick={() => setMode("heat")}
          className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium transition-colors ${mode === "heat" ? "bg-orange-400/20 text-orange-300" : "text-slate-400 hover:text-slate-200"}`}
        >
          <Flame className="h-3.5 w-3.5" /> Heatmap
        </button>
      </div>

      {mode === "heat" && shown.map((lead) => {
        if (lead.lat == null || lead.lng == null) return null;
        const p = project(lead.lat, lead.lng);
        return (
          <div
            key={lead.lead_id}
            data-testid="map-heat-blob"
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: "110px",
              height: "110px",
              background: "radial-gradient(circle, rgba(251,146,60,0.55) 0%, rgba(234,88,12,0.3) 45%, rgba(234,88,12,0) 70%)",
              mixBlendMode: "screen",
              filter: "blur(6px)",
            }}
          />
        );
      })}

      {/* product color key — click to filter the map */}
      <div className="absolute bottom-3 left-4 z-20 rounded-lg border border-white/10 bg-black/50 px-2.5 py-1.5 backdrop-blur">
        <TypeLegend onSelect={(k) => setLegendFilter((p) => (p === k ? null : k))} active={legendFilter ? [legendFilter] : []} />
      </div>

      {origin && (() => {
        const p = project(origin.lat, origin.lng);
        return (
          <>
            <div
              className="absolute rounded-full border-2 border-teal-400/40 bg-teal-400/10"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${milesToPctW}%`,
                aspectRatio: "1",
                transform: "translate(-50%, -50%)",
              }}
            />
            <div
              className="absolute h-3 w-3 rounded-full bg-teal-400 shadow-[0_0_12px_4px_rgba(45,212,191,0.6)]"
              style={{ left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -50%)" }}
            />
          </>
        );
      })()}

      {mode === "pins" && shown.map((lead) => {
        if (lead.lat == null || lead.lng == null) return null;
        const p = project(lead.lat, lead.lng);
        const cat = CATEGORIES[lead.primary_type] || CATEGORIES[lead.insurance_type] || CATEGORIES.life;
        const isActive = active === lead.lead_id;
        return (
          <button
            key={lead.lead_id}
            data-testid="map-lead-pin-marker"
            className="pin-drop absolute z-10 -translate-x-1/2 -translate-y-full focus:outline-none"
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            onClick={() => {
              setActive(isActive ? null : lead.lead_id);
              onSelect && onSelect(lead);
            }}
          >
            <MapPin
              className="h-6 w-6 drop-shadow-lg transition-transform hover:scale-125"
              style={{ color: cat.color, fill: cat.color, fillOpacity: 0.85 }}
              strokeWidth={2}
            />
            {isActive && (
              <div className="absolute bottom-full left-1/2 z-20 mb-1 w-52 -translate-x-1/2 rounded-xl border border-border bg-popover p-3 text-left shadow-2xl">
                <p className="font-heading text-sm font-semibold text-foreground">{lead.name}</p>
                <p className="text-xs text-muted-foreground">{lead.city}, {lead.state} {lead.zip}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: cat.color + "22", color: cat.color }}>
                    {cat.label}
                  </span>
                  <span className="font-mono text-xs text-foreground">{fmtMoney(lead.premium_estimate)}</span>
                </div>
                {lead.distance_miles != null && (
                  <p className="mt-1 font-mono text-[10px] text-teal-400">{lead.distance_miles} mi away</p>
                )}
                <span className="mt-1 block text-[10px]" style={{ color: STATUSES[lead.status]?.color }}>
                  ● {STATUSES[lead.status]?.label}
                </span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};
